<template>
    <fieldset :class="[$style['mds-fieldset'], sizeClass, classObject, variationClass]" :data-mds-version="version">
        <legend v-if="legend" :class="$style['mds-fieldset__legend']">
            {{ legend }}
            <span v-if="optional" :class="$style['mds-fieldset__legend-optional']">{{ optionalText }}</span>
            <abbr
                v-if="required"
                :class="$style['mds-fieldset__legend-required-indicator']"
                :title="requiredText"
                :aria-label="requiredText"
                >*</abbr
            >
            <span :class="$style['mds-fieldset--hidden']" v-if="microcopyAbove" v-html="microcopyAbove" />
            <span
                :class="$style['mds-fieldset--hidden']"
                v-for="(text, index) in errorText"
                :key="index"
                v-show="error"
                v-html="text"
            ></span>
            <span :class="$style['mds-fieldset--hidden']" v-if="microcopyBelow" v-html="microcopyBelow" />
        </legend>
        <slot name="mds-microcopy-above">
            <mds-microcopy v-if="microcopyAbove" :size="size" v-html="microcopyAbove" :on-dark="onDark" />
        </slot>
        <div v-if="horizontal" :class="[$style['mds-fieldset__horizontal'], layoutVariationClass]">
            <slot></slot>
        </div>
        <slot v-else></slot>
        <div v-if="error" :class="$style['mds-fieldset__field-error-wrapper']" role="alert" aria-live="assertive">
            <mds-field-error
                v-for="(text, index) in errorText"
                :key="index"
                v-show="error"
                :size="size"
                v-html="text"
                :on-dark="onDark"
            />
        </div>
        <slot name="mds-microcopy-below">
            <mds-microcopy v-if="microcopyBelow" :size="size" v-html="microcopyBelow" :on-dark="onDark" />
        </slot>
    </fieldset>
</template>

<script>
    import MdsFieldError from '@mds/field-error';
    import MdsMicrocopy from '@mds/microcopy';

    import packageInfo from '../package.json';
    const { name, version } = packageInfo;

    export default {
        name: 'MdsFieldset',
        components: {
            MdsFieldError,
            MdsMicrocopy,
        },
        data() {
            return {
                version: `${name}-${version}`,
            };
        },
        props: {
            error: {
                type: Boolean,
                default: false,
                docs: {
                    validation: '—',
                    description:
                        'If `true`, triggers the error state and shows the `errorText` for the fieldset. Should only be used with the `checkbox-group`, `radio-group`, or `switch-group` variations.',
                },
            },
            errorText: {
                type: Array,
                docs: {
                    validation: '—',
                    description:
                        'The error text that appears when `error=true` for the fieldset. Should only be used with the `checkbox-group`, `radio-group`, or `switch-group` variations.',
                    support: ['v-html'],
                },
            },
            hiddenLegend: {
                type: Boolean,
                default: false,
                docs: {
                    validation: '—',
                    description: 'If `true`, visibly hides the fieldset legend.',
                },
            },
            horizontal: {
                type: Boolean,
                default: false,
                docs: {
                    validation: '—',
                    description: 'If `true`, displays the fieldset contents inline horizontally.',
                },
            },
            legend: {
                type: String,
                docs: {
                    validation: '—',
                    description: 'Sets the legend for the fieldset.',
                },
            },
            microcopyAbove: {
                type: String,
                docs: {
                    validation: '—',
                    description: 'Sets the microcopy above the fieldset.',
                    support: ['v-html'],
                },
            },
            microcopyBelow: {
                type: String,
                docs: {
                    validation: '—',
                    description: 'Sets the microcopy below the fieldset.',
                    support: ['v-html'],
                },
            },
            onDark: {
                type: Boolean,
                default: false,
                docs: {
                    validation: '—',
                    description: 'If `true`, changes component to on-dark styling.',
                },
            },
            optional: {
                type: Boolean,
                default: false,
                docs: {
                    validation: '—',
                    description: 'If `true`, displays the `optionalText` on the fieldset legend.',
                },
            },
            optionalText: {
                type: String,
                default: '(Optional)',
                docs: {
                    validation: '—',
                    description: 'Sets the label text to describe a fieldset as optional.',
                },
            },
            required: {
                type: Boolean,
                default: false,
                docs: {
                    validation: '—',
                    description: 'If `true`, displays the required indicator (*) on the fieldset legend.',
                },
            },
            requiredText: {
                type: String,
                default: 'This field is required',
                docs: {
                    validation: '—',
                    description: 'Sets the `aria-label` text to describe a fieldset as required.',
                },
            },
            size: {
                type: String,
                default: 'medium',
                validator(value) {
                    return ['small', 'medium', 'large', 'touch'].indexOf(value) !== -1;
                },
                docs: {
                    validation: 'One of: `small`, `medium`, `large`, `touch`.',
                    description: 'Sets the size of the fieldset.',
                },
            },
            variation: {
                type: String,
                default: 'default',
                validator(value) {
                    return ['default', 'checkbox-group', 'radio-group', 'switch-group'].indexOf(value) !== -1;
                },
                docs: {
                    validation: 'One of: `default`, `checkbox-group`, `radio-group`, `switch-group`.',
                    description:
                        'Sets the variation for the fieldset. `default` should be used when a fieldset wraps other inputs or fieldsets.',
                },
            },
        },
        computed: {
            layoutVariationClass() {
                return this.variation !== 'default' ? this.$style[`mds-fieldset__horizontal--${this.variation}`] : null;
            },
            variationClass() {
                return this.variation !== 'default' ? this.$style[`mds-fieldset--${this.variation}`] : null;
            },
            classObject() {
                const obj = {};
                obj[this.$style['mds-fieldset--hide-legend']] = this.hiddenLegend;
                obj[this.$style['mds-component--on-dark']] = this.onDark;

                return obj;
            },
            sizeClass() {
                return this.$style[`mds-fieldset--${this.size}`];
            },
        },
    };
</script>

<style lang="scss" module>
    @import './fieldset.scss';
</style>
